import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CpuChipIcon,
  ChartBarIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ClockIcon,
  SparklesIcon,
  BeakerIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface AIAnalysisResult {
  id: string;
  type: 'financial_optimization' | 'market_prediction' | 'risk_assessment' | 'opportunity_detection';
  title: string;
  description: string;
  confidence: number;
  impact: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  recommendations: string[];
  metrics: {
    currentValue: number;
    predictedValue: number;
    improvement: number;
    timeframe: string;
  };
  actionItems: {
    id: string;
    title: string;
    priority: 'urgent' | 'high' | 'medium' | 'low';
    estimatedTime: string;
    estimatedCost?: number;
    estimatedSavings?: number;
  }[];
}

interface AIAnalysisEngineProps {
  entityId: string;
  entityType: 'department' | 'project' | 'campaign' | 'branch';
  onAnalysisComplete?: (results: AIAnalysisResult[]) => void;
}

const AIAnalysisEngine: React.FC<AIAnalysisEngineProps> = ({
  entityId,
  entityType,
  onAnalysisComplete
}) => {
  const { t, direction } = useLanguage();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [results, setResults] = useState<AIAnalysisResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  const analysisSteps = [
    'تحليل البيانات المالية...',
    'فحص الاتجاهات السوقية...',
    'تقييم المخاطر...',
    'اكتشاف الفرص...',
    'توليد التوصيات...',
    'حساب التأثير المتوقع...',
    'إنشاء خطة العمل...'
  ];

  const runAIAnalysis = async () => {
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setResults([]);

    // Simulate AI analysis process
    for (let i = 0; i < analysisSteps.length; i++) {
      setCurrentStep(analysisSteps[i]);
      setAnalysisProgress((i + 1) / analysisSteps.length * 100);
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
    }

    // Generate mock AI analysis results
    const mockResults: AIAnalysisResult[] = [
      {
        id: '1',
        type: 'financial_optimization',
        title: 'تحسين الكفاءة المالية',
        description: 'تحليل الذكاء الاصطناعي يشير إلى إمكانية تحسين الكفاءة المالية بنسبة 18% من خلال إعادة توزيع الموارد وتحسين العمليات.',
        confidence: 94,
        impact: 'high',
        category: 'تحسين مالي',
        recommendations: [
          'إعادة توزيع 15% من ميزانية التسويق التقليدي إلى التسويق الرقمي',
          'تطبيق نظام إدارة المصروفات الآلي لتقليل الهدر بنسبة 12%',
          'تحسين دورة التحصيل لتقليل المدة من 45 إلى 30 يوم'
        ],
        metrics: {
          currentValue: 73,
          predictedValue: 91,
          improvement: 18,
          timeframe: '3 أشهر'
        },
        actionItems: [
          {
            id: '1-1',
            title: 'تطبيق نظام إدارة المصروفات الآلي',
            priority: 'high',
            estimatedTime: '2-3 أسابيع',
            estimatedCost: 25000,
            estimatedSavings: 120000
          },
          {
            id: '1-2',
            title: 'إعادة هيكلة ميزانية التسويق',
            priority: 'medium',
            estimatedTime: '1 أسبوع',
            estimatedSavings: 45000
          }
        ]
      },
      {
        id: '2',
        type: 'market_prediction',
        title: 'توقعات السوق الذكية',
        description: 'النماذج التنبؤية تشير إلى نمو متوقع في القطاع بنسبة 25% خلال الربع القادم، مع فرص استثمارية واعدة.',
        confidence: 87,
        impact: 'critical',
        category: 'توقعات السوق',
        recommendations: [
          'زيادة الاستثمار في المنتجات الرقمية بنسبة 30%',
          'توسيع الفريق التقني لمواكبة النمو المتوقع',
          'تطوير شراكات استراتيجية مع الموردين المحليين'
        ],
        metrics: {
          currentValue: 2400000,
          predictedValue: 3000000,
          improvement: 25,
          timeframe: '3 أشهر'
        },
        actionItems: [
          {
            id: '2-1',
            title: 'تطوير استراتيجية المنتجات الرقمية',
            priority: 'urgent',
            estimatedTime: '4-6 أسابيع',
            estimatedCost: 150000
          }
        ]
      },
      {
        id: '3',
        type: 'risk_assessment',
        title: 'تقييم المخاطر المتقدم',
        description: 'تحليل المخاطر يظهر مخاطر متوسطة في سلسلة التوريد، مع توصيات للتخفيف من التأثير المحتمل.',
        confidence: 91,
        impact: 'medium',
        category: 'إدارة المخاطر',
        recommendations: [
          'تنويع مصادر التوريد لتقليل الاعتماد على مورد واحد',
          'إنشاء مخزون احتياطي للمواد الحرجة',
          'تطوير خطة طوارئ للتعامل مع انقطاع التوريد'
        ],
        metrics: {
          currentValue: 65,
          predictedValue: 85,
          improvement: 20,
          timeframe: '2 أشهر'
        },
        actionItems: [
          {
            id: '3-1',
            title: 'تطوير خطة تنويع الموردين',
            priority: 'high',
            estimatedTime: '3-4 أسابيع',
            estimatedCost: 75000
          }
        ]
      }
    ];

    setResults(mockResults);
    setIsAnalyzing(false);
    setShowResults(true);
    
    if (onAnalysisComplete) {
      onAnalysisComplete(mockResults);
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'critical': return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <div className="p-3 bg-white/20 rounded-xl">
              <CpuChipIcon className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">محرك التحليل الذكي</h2>
              <p className="text-purple-100">تحليل متقدم بالذكاء الاصطناعي</p>
            </div>
          </div>
          
          {!isAnalyzing && !showResults && (
            <button
              onClick={runAIAnalysis}
              className="flex items-center px-6 py-3 bg-white text-purple-600 rounded-xl hover:bg-purple-50 transition-colors font-semibold"
            >
              <SparklesIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
              بدء التحليل
            </button>
          )}
        </div>
      </div>

      {/* Analysis Progress */}
      <AnimatePresence>
        {isAnalyzing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-6 border-b border-gray-200 dark:border-gray-700"
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {currentStep}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {Math.round(analysisProgress)}%
                </span>
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <motion.div
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${analysisProgress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              
              <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse text-gray-600 dark:text-gray-400">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <BeakerIcon className="w-5 h-5" />
                </motion.div>
                <span className="text-sm">جاري معالجة البيانات...</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Analysis Results */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 space-y-6"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <LightBulbIcon className="w-6 h-6 ml-2 rtl:ml-0 rtl:mr-2 text-yellow-500" />
                نتائج التحليل الذكي
              </h3>
              <button
                onClick={runAIAnalysis}
                className="flex items-center px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              >
                <ArrowTrendingUpIcon className="w-4 h-4 ml-1 rtl:ml-0 rtl:mr-1" />
                إعادة التحليل
              </button>
            </div>

            {results.map((result, index) => (
              <motion.div
                key={result.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-gray-200 dark:border-gray-600 rounded-xl p-6 space-y-4"
              >
                {/* Result Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 rtl:space-x-reverse mb-2">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {result.title}
                      </h4>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getImpactColor(result.impact)}`}>
                        {result.impact === 'critical' ? 'حرج' :
                         result.impact === 'high' ? 'عالي' :
                         result.impact === 'medium' ? 'متوسط' : 'منخفض'}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-3">
                      {result.description}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {result.confidence}%
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">ثقة</div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {result.metrics.currentValue.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">القيمة الحالية</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {result.metrics.predictedValue.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">القيمة المتوقعة</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      +{result.metrics.improvement}%
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">التحسن</div>
                  </div>
                </div>

                {/* Recommendations */}
                <div>
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-3">التوصيات:</h5>
                  <ul className="space-y-2">
                    {result.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start space-x-2 rtl:space-x-reverse">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 dark:text-gray-400">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Items */}
                <div>
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-3">خطة العمل:</h5>
                  <div className="space-y-3">
                    {result.actionItems.map((action) => (
                      <div key={action.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 rtl:space-x-reverse mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {action.title}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(action.priority)}`}>
                              {action.priority === 'urgent' ? 'عاجل' :
                               action.priority === 'high' ? 'عالي' :
                               action.priority === 'medium' ? 'متوسط' : 'منخفض'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-500 dark:text-gray-400">
                            <span>⏱️ {action.estimatedTime}</span>
                            {action.estimatedCost && (
                              <span>💰 {formatCurrency(action.estimatedCost)}</span>
                            )}
                            {action.estimatedSavings && (
                              <span className="text-green-600 dark:text-green-400">
                                💰 توفير: {formatCurrency(action.estimatedSavings)}
                              </span>
                            )}
                          </div>
                        </div>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                          تنفيذ
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {!isAnalyzing && !showResults && (
        <div className="p-12 text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-full flex items-center justify-center">
            <RocketLaunchIcon className="w-12 h-12 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            جاهز للتحليل الذكي
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            اضغط على "بدء التحليل" للحصول على رؤى ذكية مدعومة بالذكاء الاصطناعي
          </p>
        </div>
      )}
    </div>
  );
};

export default AIAnalysisEngine;
